import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Compass, Truck, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import ListingPreviewModal from "../components/listing/ListingPreviewModal";
import LiveListingPreview from "../components/listing/LiveListingPreview";
import MissingFieldsDialog from "../components/listing/MissingFieldsDialog";
import StickyActionBar from "../components/listing/StickyActionBar";
import CreateListingToolbar from "../components/listing/CreateListingToolbar";
import CreateListingWizard from "../components/listing/CreateListingWizard";
import CategorySaleTypeSection from "../components/listing/sections/CategorySaleTypeSection";
import AuctionFieldsSection from "../components/listing/sections/AuctionFieldsSection";
import PawnFieldsSection from "../components/listing/sections/PawnFieldsSection";
import AuctionRentalBasicsCard from "../components/listing/AuctionRentalBasicsCard";
import AuctionVehicleSource from "../components/listing/AuctionVehicleSource";
import BasicsSection from "../components/listing/sections/BasicsSection";
import { CatalogOrderCodePicker } from "../components/dealer/ManufacturerCatalog";
import { formatDeliveryWeek } from "../lib/deliveryWeek";
import { BasicsCollapseContext } from "../components/listing/BasicsCollapseContext";
import { FieldSourcesContext } from "../components/listing/KiBadge";
import VoiceToListingRecorder from "../components/listing/VoiceToListingRecorder";
import ZB1ScanCard from "../components/listing/ZB1ScanCard";
import MobileZb1QrCard from "../components/workshop-orders/MobileZb1QrCard";
import AnkaufChecklistModal from "../components/listing/dispo/AnkaufChecklistModal";
import EinstandCalcBlock from "../components/listing/EinstandCalcBlock";
import BicycleDetailsSection from "../components/listing/sections/BicycleDetailsSection";
import DescriptionAndTechnicalSection from "../components/listing/sections/DescriptionAndTechnicalSection";
import FooterActionsSection from "../components/listing/sections/FooterActionsSection";
import WatermarkPreviewModal from "../components/listing/WatermarkPreviewModal";
import SaveAsTemplateDialog from "../components/listing/SaveAsTemplateDialog";
import NeuInconsistencyDialog from "../components/listing/NeuInconsistencyDialog";
import useListingDraft from "../hooks/useListingDraft";
import useEditAutosave from "../hooks/useEditAutosave";
import useListingDeepLinks from "../hooks/useListingDeepLinks";
import useListingAutofill from "../hooks/useListingAutofill";
import { submitListing } from "../lib/listingSubmit";
import { INITIAL_LISTING_FORM } from "../lib/listingFormDefaults";
import { computeMissingRequired, jumpToMissing } from "../lib/listingValidation";
import { applyZb1Result } from "../lib/applyZb1Result";
import { classifyVehicleType } from "../lib/classifyVehicleType";


/**
 * SectionToggle — schlanker Akkordeon-Header. Bewusst ohne eigenes weißes
 * Card-Hintergrund, weil die gewrappten Sub-Komponenten ihren eigenen
 * Card-Stil bereits mitbringen. Wenn zugeklappt wird nichts gerendert,
 * sodass die Form übersichtlich bleibt.
 */

export default function CreateListingPage() {
    const { id } = useParams();
    const isEdit = !!id;
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    // Feb-2026 (User-Wunsch) — „Disposition-Modus": wird die Seite über die
    // Disposition aufgerufen (`?focus=disposition`), blenden wir die Inserieren-
    // Submit-Leiste (Abbrechen / Vorschau / Änderungen speichern / Ankauf) aus —
    // sie wird nur beim Fahrzeug-Inserieren benötigt.
    const dispoMode = searchParams.get("focus") === "disposition";
    // Jun-2026 (User-Wunsch) — „Bestellfahrzeug anlegen" (EU-Fahrzeugbestellung):
    // Aufruf über `?mode=bestellung`. Zustand „Neufahrzeug" + Lager-Status
    // „Bestellung" werden vorausgewählt (siehe useListingDeepLinks) und ein
    // Hinweis-Banner mit der Ausfüll-Reihenfolge angezeigt.
    const orderMode = !isEdit && searchParams.get("mode") === "bestellung";

    // Deep-Link-Verhalten (?focus / ?sale_type / ?action=sell) → ausgelagert in
    // useListingDeepLinks (Hook-Aufruf weiter unten, nach der useState-Deklaration).

    const [form, setForm] = useState(INITIAL_LISTING_FORM);
    const [submitting, setSubmitting] = useState(false);
    // Jul-2026: „Zustand & Schadensermittlung" ist jetzt inline (siehe beforePriceSlot), kein Modal-State mehr nötig.
    // Jun-2026 (User-Wunsch P0) — `editLoaded` wird true, sobald ein bestehendes
    // Inserat geladen ist. Erst dann startet das generische Auto-Speichern.
    const [editLoaded, setEditLoaded] = useState(false);
    const [cockpitWarned, setCockpitWarned] = useState(false);  // Phase 3.54 — Tacho-Foto-Warnung
    const [complianceWarned, setComplianceWarned] = useState(false);  // Phase 4.12 (M2)
    const [previewOpen, setPreviewOpen] = useState(false);       // Phase 3.62 — Vorschau-Modal
    const [missingDialogOpen, setMissingDialogOpen] = useState(false);  // Mai-2026 — Pflichtfeld-Popup beim Speichern
    const [backendValidationErrors, setBackendValidationErrors] = useState([]);  // Feb-2026 — 422-Fehler vom Backend → Popup
    // Feb-2026 (User-Wunsch) — Vorschau-Galerie der wassermarkierten Bilder nach Veröffentlichung.
    const [watermarkPreview, setWatermarkPreview] = useState(null);  // null | { images: [...], redirect: "..." }
    // Feb-2026 (User-Wunsch) — Nach dem Veröffentlichen optional fragen, ob das
    // Inserat als wiederverwendbare Vorlage gespeichert werden soll. null | { redirect }.
    const [publishTemplatePrompt, setPublishTemplatePrompt] = useState(null);
    // Feb-2026 Iter 28 — Fahrzeug-Stammdaten ist initial offen, klappt sich aber
    // automatisch zu, sobald eine andere Top-Level-Section geöffnet wird.
    // Jul-2026 (User-Wunsch) — Im Disposition-Modus (Verkaufen/Reservieren) starten
    // ALLE Bereiche eingeklappt, nur „Disposition & Verkaufsabwicklung" ist offen.
    const [basicsOpen, setBasicsOpen] = useState(!dispoMode);
    const closeBasicsIfOpen = useCallback(() => setBasicsOpen(false), []);
    const basicsCtxValue = useMemo(() => ({ closeBasicsIfOpen }), [closeBasicsIfOpen]);
    // Feb-2026 (User-Wunsch UX) — „Nur Pflichtfelder"-Modus: blendet alle
    // optionalen Sections aus, damit die Seite minimal wirkt.
    const [essentialOnly, setEssentialOnly] = useState(() => {
        try { return localStorage.getItem("twowheels.create-listing.essential-only") === "1"; }
        catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem("twowheels.create-listing.essential-only", essentialOnly ? "1" : "0"); }
        catch { /* ignore */ }
    }, [essentialOnly]);
    // Ref auf FooterActionsSection — Sticky-Bar verschwindet, wenn dieser Bereich sichtbar wird.
    const footerActionsRef = useRef(null);
    const [savingDraft, setSavingDraft] = useState(false);
    // Feb-2026 (User-Wunsch) — „Rundgang" (Geführter Modus · Live-Vorschau ·
    // Fortschritt) wird über EINEN Button oben im Seitenkopf gesteuert und ist
    // STANDARDMÄSSIG AUS. Der frühere Toolbar-Dropdown (mit „Diese Woche/Dauerhaft
    // ausblenden") wurde entfernt. Persistenz über genau einen LocalStorage-Key:
    // "0" = sichtbar, alles andere (inkl. fehlend) = ausgeblendet.
    const HELPERS_KEY = "twowheels.create-listing.helpers-hidden";
    const [helpersHidden, setHelpersHidden] = useState(() => {
        try { return localStorage.getItem(HELPERS_KEY) !== "0"; }
        catch { return true; }
    });
    useEffect(() => {
        try { localStorage.setItem(HELPERS_KEY, helpersHidden ? "1" : "0"); }
        catch { /* ignore */ }
    }, [helpersHidden]);

    useEffect(() => {
        if (!loading && (!user || user === false)) navigate("/login");
    }, [user, loading, navigate]);

    useEffect(() => {
        if (isEdit) {
            api.get(`/listings/${id}`).then((r) => { setForm((prev) => {
                // Feb-2026 — null-Werte aus der DB auf die Formular-Defaults
                // zurückführen, damit kontrollierte Inputs nicht auf `value=null`
                // umschalten (React-Console-Warnung „uncontrolled → controlled").
                const incoming = {};
                for (const [k, v] of Object.entries(r.data || {})) {
                    incoming[k] = v === null ? (prev[k] ?? "") : v;
                }
                return { ...prev, ...incoming };
            }); setEditLoaded(true); });
        }
    }, [id, isEdit]);

    // Phase 3.50 — PLZ automatisch aus Händler-Stammdaten übernehmen + (3.52) Straße
    useEffect(() => {
        if (!isEdit && user && user.postal_code && !form.postal_code) {
            setForm((p) => ({
                ...p,
                postal_code: user.postal_code,
                location: p.location || [user.city, user.country].filter(Boolean).join(", ") || user.city || "",
                street: p.street || user.street || "",
                sharing_pickup_address: p.sharing_pickup_address || [user.street, user.postal_code, user.city].filter(Boolean).join(", "),
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isEdit]);

    // Feb-2026 (User-Wunsch 24 May) — Marktplatz-Einstellungen aus dem
    // Händlerprofil als Defaults übernehmen: Preisvorschläge, Inzahlungnahme
    // und Direkt-Chat werden standardmäßig angehakt, wenn der Händler sie
    // unter „Marktplatzeinstellungen" aktiviert hat. Läuft nur einmal beim
    // ersten Mount eines neuen Inserats — der Inserent kann jederzeit
    // wieder abwählen.
    const marketplaceDefaultsAppliedRef = useRef(false);
    useEffect(() => {
        if (marketplaceDefaultsAppliedRef.current) return;
        if (isEdit || !user) return;
        marketplaceDefaultsAppliedRef.current = true;
        setForm((p) => ({
            ...p,
            allow_offers: !!user.allow_offers,
            instant_buy_enabled: !!user.instant_buy_enabled,
            missed_call_alerts_enabled: user.missed_call_alerts_enabled !== false,
            allow_tradein: user.allow_tradein !== false,
            allow_chat: user.allow_chat !== false,
            allow_test_ride: !!user.allow_test_ride,
        }));
    }, [user, isEdit]);


    // Feb-2026 (User-Wunsch 26 May): Bei NEUEM Inserat die Kategorie automatisch
    // vorbelegen, wenn der Händler in seinem Profil nur Motorrad ODER nur Fahrrad
    // markiert hat. Bricht den Default „motorcycle" nur, wenn dealer_bicycles aktiv
    // und dealer_motorcycles deaktiviert ist.
    useEffect(() => {
        if (isEdit || !user) return;
        const hasMoto = user.dealer_motorcycles !== false;
        const hasBike = !!user.dealer_bicycles;
        if (!hasMoto && hasBike && form.category !== "bicycle") {
            setForm((p) => ({ ...p, category: "bicycle" }));
        } else if (hasMoto && !hasBike && form.category !== "motorcycle") {
            setForm((p) => ({ ...p, category: "motorcycle" }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.dealer_motorcycles, user?.dealer_bicycles, isEdit]);


    // Feb-2026 (User-Wunsch) — Per-Feld-Quelle: "ai" (KI-befüllt) oder "manual"
    // (anschließend manuell überschrieben). Felder ohne Eintrag zeigen keinen Marker.
    const [fieldSources, setFieldSources] = useState({});
    const markAiFields = useCallback((keys) => {
        setFieldSources((s) => {
            const next = { ...s };
            (keys || []).forEach((k) => {
                if (!k || typeof k !== "string" || k.startsWith("__")) return;
                // Bereits manuell überschriebene Felder NICHT zurück auf KI setzen.
                if (next[k] === "manual") return;
                next[k] = "ai";
            });
            return next;
        });
    }, []);
    // Flippt ein KI-Feld auf „manual", sobald der Nutzer es selbst ändert.
    // Felder, die nie von der KI berührt wurden, bleiben unmarkiert.
    const markManual = (k) => setFieldSources((s) => (k in s ? { ...s, [k]: "manual" } : s));

    const update = (k, v) => {
        // Phase 3.50 — PS auto-Berechnung aus KW (Faktor 1.36)
        if (k === "power_kw") {
            const kw = parseFloat(v);
            setForm((p) => ({
                ...p,
                power_kw: v,
                power_hp: kw > 0 ? Math.round(kw * 1.35962) : p.power_hp,
            }));
            markManual("power_kw");
            markManual("power_hp");
            return;
        }
        // Brand-Wechsel räumt das Modell, damit Auto-Vorschläge frisch passen
        if (k === "brand") {
            setForm((p) => ({ ...p, brand: v, model: "" }));
            markManual("brand");
            return;
        }
        setForm((p) => ({ ...p, [k]: v }));
        markManual(k);
    };

    // KI-Auto-Fill (KBA / Werks-Daten / Titel / Hersteller→Marke) + Deep-Link-
    // Verhalten (?focus / ?sale_type) sind in Hooks ausgelagert.
    useListingDeepLinks({ searchParams, isEdit, setForm });
    useListingAutofill({ form, setForm, isEdit, user, markAiFields, update });

    // ZB1/COC-Scan-Ergebnis anwenden — ausgelagert nach lib/applyZb1Result.
    const zb1OnResult = (fields) => {
        applyZb1Result(fields, { setForm, markAiFields });
        // Jul-2026 (User-Wunsch) — HSN/TSN beim ZB1-Scan automatisch gegen die interne
        // Fahrzeugdatenbank abfragen und fehlende Eckdaten (Marke/Modell/ccm/kW) vorbefüllen.
        if (fields.hsn && fields.tsn) {
            api.get("/hsn-tsn/lookup", { params: { hsn: fields.hsn, tsn: fields.tsn } })
                .then((res) => {
                    if (!res.data?.found) return;
                    const r = res.data.result || {};
                    const cleanBrand = (r.hersteller || "").replace(/\s*\(.*?\)\s*/g, " ").trim();
                    const applied = [];
                    setForm((p) => {
                        const patch = {};
                        if (!p.brand && cleanBrand) { patch.brand = cleanBrand; applied.push("brand"); }
                        if (!p.model && r.bezeichnung) { patch.model = r.bezeichnung; applied.push("model"); }
                        if (!p.displacement && r.ccm) { patch.displacement = String(r.ccm); applied.push("displacement"); }
                        if (!p.power_kw && r.kw) { patch.power_kw = String(r.kw); applied.push("power_kw"); }
                        return Object.keys(patch).length ? { ...p, ...patch } : p;
                    });
                    if (applied.length) {
                        markAiFields?.(applied);
                        toast.message(`HSN/TSN-Datenbank: ${applied.length} Feld(er) ergänzt (${r.hersteller || ""} ${r.bezeichnung || ""})`.trim());
                    }
                })
                .catch(() => {});
        }
        // Jul-2026 (User-Wunsch · 451) — Fahrzeugtyp per gemeinsamer KI-Klassifikation
        // setzen (identisch zum Eintausch-Scan).
        classifyVehicleType({
            brand: fields.brand || fields.manufacturer,
            model: fields.model,
            vehicle_class: fields.vehicle_class,
        }).then((res) => {
            if (res?.vehicle_type) {
                setForm((p) => ({ ...p, vehicle_type: res.vehicle_type }));
                markAiFields?.(["vehicle_type"]);
                toast.message(`Fahrzeugtyp erkannt: „${res.vehicle_type}"${res.source === "ai" ? " (KI)" : ""}`);
            }
        }).catch(() => {});
    };

    // Phase 3.97 — Backend-Sync für Entwürfe (useListingDraft Hook)
    // Sync zu MongoDB damit Händler geräteübergreifend weitermachen können;
    // localStorage bleibt Offline-Fallback. Auto-Save alle 30 s.
    const draft = useListingDraft({ user, isEdit, form });
    // Jun-2026 (User-Wunsch P0) — Generisches Auto-Speichern aller editierbaren
    // Felder im Bearbeiten-/Disposition-Modus (behebt Datenverlust beim Ändern
    // z. B. des Zustands NW/TZ/VFW/GW ohne manuelles Speichern).
    const { saveState: editSaveState } = useEditAutosave({ form, isEdit, ready: editLoaded, listingId: id });
    const draftSavedAt = draft.savedAt;
    const draftRestoreCandidate = draft.restoreCandidate;
    const saveDraftNow = draft.saveNow;
    const restoreDraft = () => draft.restore((restored) => setForm((p) => ({ ...p, ...restored })));
    const discardDraft = () => { draft.discard(); };

    // Phase 3.95 — Draft-Modus-Heuristik: wenn die drei Kern-Felder (Title,
    // Preis, Bilder) komplett leer sind, ist „Entwurf speichern" sinnvoller
    // als „Veröffentlichen", weil der Pflichtfeld-Toast sonst sofort feuert.
    const isDraftMode = !isEdit
        && !form.title
        && !form.price
        && (!Array.isArray(form.images) || form.images.length === 0);

    // Mai-2026 — Live-Berechnung der noch fehlenden Pflichtfelder für den
    // Banner ganz oben + die roten Hervorhebungen. Sehr leichtgewichtig:
    // nur Bool-Checks; teure Berechnungen passieren nicht. Wird intentional
    // OHNE useMemo gebaut, weil die Inputs ohnehin bei jedem Render geprüft
    // werden.
    const isMotoCat = form.category === "motorcycle";
    const isDealerCat = user?.role === "b2b";
    // Jun-2026 (User-Wunsch) — „Spezial-Modus": bei Auktion, Sharing/Vermietung
    // ODER Pfand/Refinanzieren wird das komplette Fahrzeug-Formular ausgeblendet;
    // nur die Eckdaten + die typ-spezifischen Felder bleiben sichtbar (plus ein
    // Button zurück zum klassischen Festpreis-Inserat). Pfand ist auch für
    // Privatkunden (B2C) verfügbar, Auktion/Vermietung bleiben B2B.
    const isSpecialSaleType = form.sale_type === "auction" || form.sale_type === "rental" || form.sale_type === "pawn";
    const specialMode = (isSpecialSaleType && isDealerCat) || form.sale_type === "pawn";
    const missingRequired = computeMissingRequired(form, { isMotoCat, isDealerCat, specialMode });
    const missingKeys = new Set(missingRequired.map((m) => m.key));

    // Feb-2026 (User-Wunsch P2) — Inkonsistenz-Warndialog: „Neufahrzeug" mit
    // km > 0 oder Vorbesitzer > 0 ist widersprüchlich. Vor dem Veröffentlichen
    // nachfragen („Trotzdem als Neufahrzeug veröffentlichen?").
    const [neuWarnOpen, setNeuWarnOpen] = useState(false);
    const neuConfirmedRef = useRef(false);
    const isNeuInconsistent = () => {
        if (form.condition !== "Neufahrzeug") return false;
        const km = parseInt(form.mileage, 10) || 0;
        const owners = parseInt(form.previous_owners, 10) || 0;
        return km > 0 || owners > 0;
    };

    const runSubmit = (e) => submitListing(e, {
        form, user, id, isEdit, navigate, t, draft,
        cockpitWarned, complianceWarned,
        setSubmitting, setMissingDialogOpen, setBackendValidationErrors,
        setCockpitWarned, setComplianceWarned,
        setWatermarkPreview,
        onPublishedPromptTemplate: (redirect) => setPublishTemplatePrompt({ redirect }),
        toast, api,
    });

    const submit = (e) => {
        if (isNeuInconsistent() && !neuConfirmedRef.current) {
            if (e && e.preventDefault) e.preventDefault();
            setNeuWarnOpen(true);
            return;
        }
        return runSubmit(e || { preventDefault: () => {} });
    };

    // Jun-2026 (User-Wunsch) — Zurück zum klassischen Festpreis-Inserat: setzt die
    // Verkaufsart auf „fixed" und deaktiviert Sharing, sodass das vollständige
    // Fahrzeug-Formular wieder erscheint.
    const backToFixed = () => setForm((p) => ({ ...p, sale_type: "fixed", sharing_enabled: false }));

    // Jun-2026 (User-Wunsch) — Auktionen docken an ein Bestandsfahrzeug an:
    // (a) Fahrzeug auswählen → Daten übernehmen; (b) bei bereits vorhandenen Daten
    // (Bearbeiten/Konvertieren) Eckdaten ausblenden. „Manuell anlegen" als Fallback.
    const [auctionVehicleId, setAuctionVehicleId] = useState(null);
    const [auctionManualEntry, setAuctionManualEntry] = useState(false);
    const selectAuctionVehicle = (l) => {
        setForm((p) => ({
            ...p,
            title: l.title || `${l.brand || ""} ${l.model || ""}`.trim(),
            brand: l.brand || "",
            model: l.model || "",
            year: l.year || p.year,
            first_registration_date: l.first_registration_date || p.first_registration_date,
            location: l.location || "",
            images: Array.isArray(l.images) ? l.images : [],
            category: l.category || p.category,
            mileage: l.mileage ?? p.mileage,
            source_listing_id: l.id,
        }));
        setAuctionVehicleId(l.id);
    };
    const clearAuctionVehicle = () => {
        setAuctionVehicleId(null);
        setForm((p) => ({ ...p, title: "", brand: "", model: "", images: [], location: "", source_listing_id: null }));
    };


    if (!user || user === false) return <div className="p-12">{t("create_listing.loading")}</div>;
    const isMoto = form.category === "motorcycle";
    const isAuction = form.sale_type === "auction";
    const isDealer = user.role === "b2b";
    const auctionsAllowed = isDealer && user.allow_auctions !== false;

    // Default auction end = 7 days from now
    const defaultAuctionEnd = () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        d.setSeconds(0, 0);
        return d.toISOString().slice(0, 16);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="create-listing-page">
            <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2 justify-between">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">
                        {isEdit ? t("create_listing.title_edit") : t("create_listing.title_new")}
                    </h1>
                    {isEdit && form.internal_number && (
                        <span
                            className="inline-flex items-center gap-1.5 bg-[#111827] text-[#FFCC00] px-3 py-1 rounded-sm font-mono-num text-sm font-bold tracking-wider"
                            title="Interne Fahrzeugnummer — am Präfix erkennen Sie den Händler."
                            data-testid="listing-internal-number"
                        >
                            Nr. {form.internal_number}
                        </span>
                    )}
                </div>
            </div>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-gray-600">{t("create_listing.subtitle")}</p>
                <div className="flex items-center gap-3">
                {isEdit && editSaveState !== "idle" && (
                    <span
                        className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${editSaveState === "saving" ? "text-gray-500" : "text-emerald-600"}`}
                        data-testid="listing-edit-autosave-indicator"
                    >
                        <span className={`w-2 h-2 rounded-full ${editSaveState === "saving" ? "bg-gray-400 animate-pulse" : "bg-emerald-500"}`} />
                        {editSaveState === "saving" ? "Speichert …" : "Automatisch gespeichert"}
                    </span>
                )}
                {/* Feb-2026 (User-Wunsch) — „Rundgang"-Button (ersetzt den
                    Toolbar-Toggle „Geführter Modus"). Standardmäßig AUS; blendet
                    Geführten Modus, Live-Vorschau und Fortschritt gemeinsam ein/aus. */}
                <button
                    type="button"
                    onClick={() => setHelpersHidden((v) => !v)}
                    className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm text-[11px] font-bold uppercase tracking-wider border transition-colors ${helpersHidden
                        ? "bg-white border-gray-200 text-gray-700 hover:border-[#111827] hover:text-[#111827]"
                        : "bg-[#FFCC00] border-[#111827] text-[#111827]"}`}
                    data-testid="listing-tour-toggle-btn"
                    aria-pressed={!helpersHidden}
                    title="Rundgang ein-/ausblenden: Geführter Modus, Live-Vorschau und Fortschritts-Anzeige"
                >
                    <Compass className="w-3.5 h-3.5" />
                    {helpersHidden ? "Rundgang starten" : "Rundgang aktiv"}
                </button>
                </div>
            </div>

            {/* Feb-2026 (User-Wunsch 24 May Iter 20) — Qualität UND KI-Sprachaufnahme
                jetzt visuell vereint in EINER durchgehenden Karte mit Trenner
                in der Mitte (statt zwei separater Boxen). Immer side-by-side
                ab md-Breakpoint. */}
            {/* Feb-2026 (User-Wunsch 24 May Iter 24): Quality+Voice-Card aus
                dem Top-Bereich entfernt — wurde in die Form verschoben,
                direkt vor Toolbar+ZB1-Scanner+Fahrzeug-Stammdaten. */}

            {/* Feb-2026 (User-Wunsch 24 May Iter 21): Toolbar (Vorlage /
                Nur Pflichtfelder / Hilfsanzeigen) wurde in die Form
                verschoben — direkt vor ZB1-Scan + Fahrzeug-Stammdaten. */}

            {/* Multi-Doc Scanner — COC / ZB1 (Zulassung) / ZB2 (Fahrzeugbrief) / Service-Heft.
                KI klassifiziert + extrahiert die Daten automatisch und archiviert die Dokumente. */}
            {/* Feb-2026 (User-Wunsch 24 May Iter 19): ZB1-Scan-Karte aus dem
                Top-Bereich entfernt — wird jetzt INNERHALB der Form direkt
                über „Fahrzeug-Stammdaten" (BasicsSection) gerendert. */}

            {/* Feb-2026 — Geführter Modus / Wizard (nur bei neuen Inseraten) */}
            {!helpersHidden && <CreateListingWizard form={form} isEdit={isEdit} />}

            {/* Mai-2026 — Qualitäts-Score wurde nach oben (neben KI-Sprachaufnahme)
                verschoben (User-Wunsch 24 May Iter 19). */}

            <div className={helpersHidden
                ? "grid grid-cols-1 gap-8 items-start"
                : "grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-start"}>
            <form onSubmit={submit} noValidate className="space-y-6 min-w-0" data-testid="create-listing-form">
                <FieldSourcesContext.Provider value={fieldSources}>
                {/* Feb-2026 — Draft-Restore-Banner wurde in die konsolidierte
                    CreateListingToolbar oben verschoben. */}
                {/* Auto-Save Indikator (dezent) */}
                {draftSavedAt && !isEdit && !helpersHidden && (
                    <div className="text-[10px] text-gray-400 -mt-3" data-testid="draft-autosave-indicator">
                        Entwurf automatisch gespeichert um {draftSavedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr.
                    </div>
                )}

                {/* Mai-2026 — Pflichtfelder-Status. Bei fehlenden Feldern erscheint
                    ein Popup beim Speichern (siehe MissingFieldsDialog am Seitenende),
                    daher zeigen wir hier nur den grünen „Alles okay"-Badge inline. */}
                {missingRequired.length === 0 && (
                    <div
                        className="bg-emerald-50 border-2 border-emerald-300 rounded-sm p-3 flex items-center gap-2"
                        data-testid="required-fields-banner-ok"
                    >
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold">✓</span>
                        <span className="font-display font-black text-sm text-emerald-900">Alle Pflichtfelder ausgefüllt — bereit zum Veröffentlichen.</span>
                    </div>
                )}

                {/* Feb-2026 (User-Wunsch 25 May): Smart-Quick-Publish-Karte entfernt
                    — Voice + Scan + manuelle Quicklinks decken den Use-Case bereits ab. */}

                {/* Phase 4.20 — ZB1 / COC scannen + Phase 4.16 KI-Sprachaufnahme:
                    Mai-2026 (User-Wunsch 21 May Iteration 7) — beide Karten als Slots
                    in CategorySaleTypeSection eingebettet (ZB1 unter Kategorie, nur Motorrad;
                    Voice unter Verkaufsart). */}

                {/* Jun-2026 (User-Wunsch) — Banner für EU-Fahrzeugbestellung. */}
                {orderMode && (
                    <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 w-full" data-testid="order-vehicle-banner">
                        <div className="w-full">
                            <div className="flex items-center gap-2 text-amber-900 font-bold text-xs uppercase tracking-wider">
                                <Truck className="w-4 h-4 shrink-0" /> EU-Fahrzeugbestellung — Bestellfahrzeug anlegen
                            </div>
                            <p className="text-[11px] text-amber-800 mt-1 leading-snug">
                                <b>Neufahrzeug</b> · Lager-Status <b>Bestellung</b> vorbelegt. Reihenfolge:
                                Marke · Modell (D.3) · Fahrzeugtyp → Farbe → Hubraum &amp; Leistung → Listenpreis. Danach Verkaufsprozess starten.
                            </p>
                            <div className="mt-2 max-w-xl">
                                <CatalogOrderCodePicker onPick={(it) => {
                                    setForm((p) => ({
                                        ...p,
                                        brand: it.manufacturer || p.brand,
                                        model: it.model || p.model,
                                        dispo_order_code: it.order_code || p.dispo_order_code,
                                        earliest_delivery: it.earliest_delivery || "",
                                        title: p.title || `${it.manufacturer || ""} ${it.model || ""}`.trim(),
                                    }));
                                    toast.success(`Übernommen: ${it.model} (${it.order_code})`);
                                }} />
                                {form.earliest_delivery && (
                                    <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm px-2 py-0.5" data-testid="order-earliest-delivery">
                                        <Truck className="w-3.5 h-3.5" /> Früheste Lieferung: {formatDeliveryWeek(form.earliest_delivery)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Category + Sale type + Auctions audience + Sharing fields */}
                <CategorySaleTypeSection
                    form={form}
                    update={update}
                    isMoto={isMoto}
                    isAuction={isAuction}
                    isDealer={isDealer}
                    auctionsAllowed={auctionsAllowed}
                    defaultAuctionEnd={defaultAuctionEnd}
                    user={user}
                    afterCategorySlot={null}
                    afterSaleTypeSlot={null}
                />

                {/* Jun-2026 (User-Wunsch) — Spezial-Modus (Auktion/Sharing): nur
                    Eckdaten + auktions-/mietspezifische Felder, der Rest des
                    Fahrzeug-Formulars wird ausgeblendet. */}
                {specialMode ? (
                    <>
                        {isDealer && isAuction && (
                            <AuctionFieldsSection form={form} update={update} isAuction={isAuction} />
                        )}
                        {form.sale_type === "pawn" && (
                            <PawnFieldsSection form={form} update={update} />
                        )}
                        {specialMode && (
                            <AuctionVehicleSource
                                userId={user.id}
                                form={form}
                                mode={form.sale_type}
                                selectedId={auctionVehicleId}
                                onSelect={selectAuctionVehicle}
                                onClear={clearAuctionVehicle}
                                manualEntry={auctionManualEntry}
                                onToggleManual={() => setAuctionManualEntry((v) => !v)}
                            />
                        )}
                        <AuctionRentalBasicsCard
                            form={form}
                            setForm={setForm}
                            update={update}
                            isMoto={isMoto}
                            mode={form.sale_type}
                            onBackToFixed={backToFixed}
                            footerActionsRef={footerActionsRef}
                            submitting={submitting}
                            isEdit={isEdit}
                            showEckdaten={!specialMode || auctionManualEntry}
                        />
                    </>
                ) : (
                <>
                {/* Auction-specific fields */}
                {isDealer && isAuction && (
                    <AuctionFieldsSection form={form} update={update} isAuction={isAuction} />
                )}

                {/* Feb-2026 (User-Wunsch 24 May Iter 25) — Quality+Voice-Card
                    wurde nach UNTEN verschoben (direkt über Fahrzeug-Stammdaten,
                    NACH dem ZB1-Scanner). Sortierung im Pre-Form-Stack:
                    Toolbar → ZB1-Scanner → Quality+Voice → Stammdaten. */}

                {/* Toolbar (3 gleich breite Chips: Vorlage / Nur Pflichtfelder
                    / Hilfsanzeigen) — harmonisiert mit gleicher Border-Radius
                    und Spacing wie die folgenden Cards. */}
                <CreateListingToolbar
                    isEdit={isEdit}
                    setForm={setForm}
                    form={form}
                    essentialOnly={essentialOnly}
                    setEssentialOnly={setEssentialOnly}
                    draftRestoreCandidate={draftRestoreCandidate}
                    discardDraft={discardDraft}
                    restoreDraft={restoreDraft}
                />

                {/* Multi-Doc-Scanner (ZB1/COC/ZB2/Service-Heft)
                    Feb-2026 Iter 28 (User-Wunsch 25 May): KI-Sprachaufnahme als
                    `bottomSlot` direkt unter den Quicklinks eingebettet, damit
                    Scan + Sprache + Manuell als 3 gleichwertige Eingabe-Pfade
                    nebeneinander stehen. */}
                {!isEdit && !orderMode && (
                    <div className="mb-3" data-testid="zb1-scan-wrap">
                        {/* Jul-2026 (User-Wunsch) — Handy-QR („Code für Handy scannen") ist jetzt
                            IN den Scan-Block integriert (qrSlot), keine separate Box mehr darüber. */}
                        <ZB1ScanCard
                            onResult={zb1OnResult}
                            qrSlot={(
                              <MobileZb1QrCard
                                variant="inline"
                                purpose="papers"
                                onScanned={(res) => {
                                    const merged = {};
                                    const docsForForm = [];
                                    const GALLERY_ORDER = ["VORNE", "LINKS", "RECHTS", "HINTEN", "OBEN", "TACHO", "SCHADEN 1", "SCHADEN 2"];
                                    const photos = [];
                                    (res?.documents || []).forEach((d) => {
                                        if ((d.kind === "zb1" || d.kind === "coc") && d.fields) {
                                            Object.entries(d.fields).forEach(([k, v]) => {
                                                if (["confidence", "document_type"].includes(k)) return;
                                                if (v != null && v !== "" && !(k in merged)) merged[k] = v;
                                            });
                                        }
                                        const doc = d.document || {};
                                        if (d.kind === "photo" && doc.url) {
                                            photos.push({ url: doc.url, label: (d.label || doc.label || "").toUpperCase() });
                                        } else if (doc.url) {
                                            docsForForm.push({ url: doc.url, name: doc.name, kind: doc.kind || d.kind });
                                        }
                                    });
                                    if (docsForForm.length) { merged.__zb1_documents = docsForForm; merged.__zb1_document = docsForForm[0]; }
                                    if (Object.keys(merged).length) zb1OnResult(merged);
                                    // Jul-2026 (User-Wunsch) — Fahrzeugfotos als sortierte Inserat-Galerie übernehmen.
                                    if (photos.length) {
                                        photos.sort((a, b) => {
                                            const ia = GALLERY_ORDER.indexOf(a.label); const ib = GALLERY_ORDER.indexOf(b.label);
                                            return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
                                        });
                                        setForm((p) => {
                                            const existImgs = Array.isArray(p.images) ? p.images : [];
                                            const existLabels = Array.isArray(p.image_labels) ? p.image_labels : [];
                                            const existSet = new Set(existImgs);
                                            const newImgs = [...existImgs];
                                            const newLabels = [...existLabels];
                                            while (newLabels.length < newImgs.length) newLabels.push("");
                                            photos.forEach((ph) => {
                                                if (existSet.has(ph.url)) return;
                                                newImgs.push(ph.url); newLabels.push(ph.label || ""); existSet.add(ph.url);
                                            });
                                            return { ...p, images: newImgs, image_labels: newLabels };
                                        });
                                    }
                                    if (Object.keys(merged).length || photos.length) {
                                        toast.success(`Handy-Scan übernommen${photos.length ? ` · ${photos.length} Foto(s) in die Galerie` : ""}.`);
                                    } else {
                                        toast.message("Scan empfangen — keine Daten erkannt.");
                                    }
                                }}
                              />
                            )}
                            bottomSlot={(
                                <VoiceToListingRecorder
                                    onResult={(fields) => {
                                        setForm((p) => {
                                            const next = { ...p };
                                            if (fields.brand) next.brand = fields.brand;
                                            if (fields.model) next.model = fields.model;
                                            if (fields.year) next.year = fields.year;
                                            if (fields.mileage) next.mileage = fields.mileage;
                                            if (fields.price) next.price = fields.price;
                                            if (fields.condition) next.condition = fields.condition;
                                            if (fields.fuel) next.fuel = fields.fuel;
                                            if (fields.description) next.description = fields.description;
                                            if (Array.isArray(fields.features) && fields.features.length) {
                                                const existing = Array.isArray(p.features) ? p.features : [];
                                                next.features = Array.from(new Set([...existing, ...fields.features]));
                                            }
                                            if (!p.title && next.brand && next.model) {
                                                next.title = `${next.brand} ${next.model}${next.year ? " " + next.year : ""}`;
                                            }
                                            return next;
                                        });
                                        const voiceKeys = ["brand", "model", "year", "mileage", "price", "condition", "fuel", "description"]
                                            .filter((k) => fields[k] != null && fields[k] !== "");
                                        if (Array.isArray(fields.features) && fields.features.length) voiceKeys.push("features");
                                        if (fields.brand && fields.model) voiceKeys.push("title");
                                        markAiFields(voiceKeys);
                                    }}
                                />
                            )}
                        />
                    </div>
                )}

                {/* Voice-to-Listing — Feb-2026 Iter 28 (User-Wunsch 25 May):
                    in den ZB1ScanCard-`bottomSlot` verschoben (siehe oben), damit
                    Scan + Sprache + Manuell als gleichwertige Eingabepfade
                    nebeneinander stehen. Standalone-Karte entfernt. */}

                {/* Feb-2026 Iter 28 — Fahrzeug-Stammdaten klappt automatisch zu,
                    sobald eine der „unteren" Top-Level-Sections (Ausstattung, Service,
                    Bilder, Preise, Werbung, Disposition …) aufgeklappt wird. Steuerung
                    über `basicsOpen` + `BasicsCollapseContext`. */}
                {/* Fahrzeug-Stammdaten — extracted Phase 4.6 */}
                <BasicsCollapseContext.Provider value={basicsCtxValue}>
                <BasicsSection
                    form={form}
                    setForm={setForm}
                    update={update}
                    markAiFields={markAiFields}
                    user={user}
                    isMoto={isMoto}
                    isAuction={isAuction}
                    isDealer={isDealer}
                    missingKeys={missingKeys}
                    essentialOnly={essentialOnly || orderMode}
                    orderMode={orderMode}
                    open={basicsOpen}
                    onOpenChange={setBasicsOpen}
                />

                {/* Mai-2026 — Zb1ExtendedFieldsSection wurde nach BasicsSection (direkt unter Unfall/Schaden) verschoben */}

                {/* Fahrrad-Details — extracted Phase 4.6 */}
                <BicycleDetailsSection form={form} update={update} isMoto={isMoto} />

                {/* Jul-2026 (User-Wunsch) — „Zustand & Schadensermittlung" ist jetzt
                    inline auf-/einklappbar direkt ÜBER „Preise & Vermittlungsprovision"
                    (siehe beforePriceSlot der FooterActionsSection). */}

                {/* Feb-2026 (User-Wunsch 24 May Iteration 10) — „Marktplatz-Verhalten"
                    aufgelöst. Alle 3 Toggles (Preisvorschläge, Inzahlungnahme, Direkt-Chat)
                    leben jetzt im Bereich „Werbung & Reichweite" (AdReachSection). */}

                {/* Phase 3.71 — Direkt-Chat / Sharing / Standort wurden nach unten verschoben (User-Wunsch 18 May 2026) */}

                {/* Feb-2026 (User-Wunsch 27.05.2026) — „Zuständiger Verkäufer"
                    wurde in die Disposition-Section verschoben
                    (siehe PortalTransactionSection.jsx). */}

                {/* Description + Technical (Motor/Service/Accessories) — extracted Phase 4.0.
                    Jul-2026 (User-Wunsch): Im Bestellmodus wird die Sektion angezeigt,
                    aber ExtraTechnicalSection blendet dort alles außer „Ausstattung &
                    Zubehör" aus (kompakte Bestellung). */}
                {!essentialOnly && (
                    <DescriptionAndTechnicalSection form={form} update={update} setForm={setForm} isMoto={isMoto} user={user} orderMode={orderMode} />
                )}

                {/* Footer Actions — Aktionen, Bilder, Werbung, Disposition, eBay, Submit, Protokoll */}
                <div ref={footerActionsRef}>
                <FooterActionsSection
                    form={form}
                    setForm={setForm}
                    update={update}
                    user={user}
                    isDealer={isDealer}
                    isEdit={isEdit}
                    id={id}
                    submitting={submitting}
                    isDraftMode={isDraftMode}
                    saveDraftNow={saveDraftNow}
                    setPreviewOpen={setPreviewOpen}
                    navigate={navigate}
                    essentialOnly={essentialOnly || orderMode}
                    orderMode={orderMode}
                    dispoMode={dispoMode}
                    beforePriceSlot={!essentialOnly && !orderMode && (
                        <>
                        <AnkaufChecklistModal
                            inline
                            startOpen={!!form.condition_assessment}
                            title="Zustand & Schadensermittlung"
                            vehicle={{
                                manufacturer: form.brand, brand: form.brand, model: form.model,
                                fuel: form.fuel, drive_type: form.drive_type,
                                displacement: form.displacement, ccm: form.displacement,
                                vin: form.vin, first_registration_date: form.first_registration_date,
                                mileage: form.mileage,
                            }}
                            initialCl={form.condition_assessment?.cl || null}
                            initialTechnik={form.condition_assessment?.technik_optik || null}
                            onChange={(data) => {
                                const dedTotal = Number(data?.deduction_total ?? data?.technik_optik?.estimate?.total ?? 0);
                                update("condition_assessment", { cl: data, technik_optik: data.technik_optik, deduction_total: dedTotal, include_in_ankauf: !!data.include_in_ankauf, complete: !!data.complete });
                            }}
                        />
                        {/* Jul-2026 (User-Wunsch) — „Einstand & Kalkulation" in den Bereich
                            „Zustand & Schadensermittlung" integriert (direkt darunter). */}
                        {isDealer && (
                            <EinstandCalcBlock form={form} update={update} listingId={isEdit ? id : null} />
                        )}
                        </>
                    )}
                />
                </div>
                </BasicsCollapseContext.Provider>
                </>
                )}
                </FieldSourcesContext.Provider>
            </form>

            {!helpersHidden && <LiveListingPreview form={form} />}
            </div>

            {/* Feb-2026 (User-Wunsch UX) — Sticky-Submit-Leiste am unteren Bildschirmrand.
                Im Disposition-Modus (`?focus=disposition`) komplett ausgeblendet. */}
            {!dispoMode && (
            <StickyActionBar
                missingCount={missingRequired.length}
                onSaveDraft={async () => {
                    setSavingDraft(true);
                    try { await saveDraftNow(); }
                    finally { setSavingDraft(false); }
                }}
                onSubmit={() => {
                    const formEl = document.querySelector('form[data-testid="create-listing-form"]');
                    if (formEl) formEl.requestSubmit ? formEl.requestSubmit() : formEl.submit();
                }}
                onCancel={() => navigate(-1)}
                onPreview={() => {
                    // Re-use the existing footer preview button so the modal logic
                    // stays the single source of truth (kein Doppel-State).
                    const btn = document.querySelector('[data-testid="preview-listing-btn"]');
                    if (btn) btn.click();
                }}
                onAnkauf={() => {
                    // Trigger der existierenden generateAnkauf-Logik in FooterActionsSection.
                    const btn = document.querySelector('[data-testid="ankauf-create-btn"]');
                    if (btn) btn.click();
                }}
                isDealer={user?.role === "b2b" && !specialMode && !orderMode}
                submitting={submitting}
                isEdit={isEdit}
                savingDraft={savingDraft}
                footerRef={footerActionsRef}
            />
            )}

            <ListingPreviewModal
                form={form}
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                onConfirm={() => {
                    setPreviewOpen(false);
                    // Bypass cockpit-warning second-confirm when user has explicitly clicked
                    // „Jetzt veröffentlichen" in the preview — they have already reviewed.
                    setCockpitWarned(true);
                    // Schedule submit on the next tick so state is committed first.
                    setTimeout(() => {
                        const formEl = document.querySelector('form[data-testid="create-listing-form"]');
                        if (formEl) {
                            formEl.requestSubmit ? formEl.requestSubmit() : formEl.submit();
                        }
                    }, 0);
                }}
                submitting={submitting}
                isEdit={isEdit}
                user={user}
            />

            {/* Mai-2026 — Pflichtfeld-Popup: erscheint vor Submit bei fehlenden Felder
                oder NACH Backend-422-Antwort mit den Backend-Validation-Fehlern. */}
            <MissingFieldsDialog
                open={missingDialogOpen}
                onClose={() => { setMissingDialogOpen(false); setBackendValidationErrors([]); }}
                missing={backendValidationErrors.length > 0 ? backendValidationErrors : missingRequired}
                jumpTo={(selector) => { setMissingDialogOpen(false); setBackendValidationErrors([]); setTimeout(() => jumpToMissing(selector), 50); }}
            />

            {/* Feb-2026 (User-Wunsch) — Wasserzeichen-Vorschau nach Veröffentlichung */}
            {watermarkPreview && (
                <WatermarkPreviewModal
                    images={watermarkPreview.images || []}
                    onContinue={() => {
                        const redirect = watermarkPreview.redirect || "/dashboard?tab=listings";
                        setWatermarkPreview(null);
                        // Bei neuen Inseraten erst den „Als Vorlage speichern?"-Prompt
                        // zeigen, danach navigiert dessen onOpenChange weiter.
                        if (!isEdit) setPublishTemplatePrompt({ redirect });
                        else navigate(redirect);
                    }}
                />
            )}

            {/* Feb-2026 (User-Wunsch) — Auto-Prompt „Als Vorlage speichern?" direkt
                nach dem Veröffentlichen. Speichern ODER „Nein, danke" schließt den
                Dialog und navigiert anschließend zum Ziel. */}
            {publishTemplatePrompt && (
                <SaveAsTemplateDialog
                    open
                    published
                    form={form}
                    onOpenChange={(v) => {
                        if (!v) {
                            const r = publishTemplatePrompt.redirect || "/dashboard?tab=listings";
                            setPublishTemplatePrompt(null);
                            navigate(r);
                        }
                    }}
                />
            )}

            {/* Feb-2026 (User-Wunsch P2) — Neufahrzeug-Inkonsistenz-Warndialog */}
            {neuWarnOpen && (
                <NeuInconsistencyDialog
                    form={form}
                    onCancel={() => setNeuWarnOpen(false)}
                    onConfirm={() => { neuConfirmedRef.current = true; setNeuWarnOpen(false); runSubmit({ preventDefault: () => {} }); }}
                />
            )}
        </div>
    );
}
